import Itly from 'itly';
import React, { useRef, useMemo, useState, useCallback } from 'react';
import {
  ChatLayout,
  ChatContainer,
  ChatHeader,
  ChatTitle,
  ChatBody,
  ScrollSpacer,
  ChatOverlay,
  ChatTitleContainer
} from './styles';
import { getSplitUserKey } from 'au-js-sdk/lib/services/split.io/cookies';
import { MessageBubble } from '../MessageBubble';
import { useChatContext } from '../ChatContext';
import { TypingBubble } from '../TypingDots';
import { ChatFooter } from '../ChatFooter';
import { SampleQuestions } from '../SampleQuestions';
import { ChatClose } from '../ChatClose';
import { useLockScrollWithOverscroll } from 'components/hooks/useLockScrollWithOverscroll';
import { useChatWindowLockScroll } from './useChatWindowLockScroll';
import { DeviceSize, useWindowSize } from 'hooks/useWindowSize';
import { ChatExpand } from '../ChatExpand';
import { ChatBetaNote } from '../ChatBetaNote';

const ChatWindow = (): JSX.Element => {
  const {
    inputValue,
    setInputValue,
    messages,
    setMessages,
    conversationId,
    setConversationId,
    setIsLoading,
    isExpanded
  } = useChatContext();

  const chatBodyRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const [spacerHeight, setSpacerHeight] = useState(0);
  const [isWaitingOnFirstChunk, setIsWaitingOnFirstChunk] = useState(false);

  const { deviceSize } = useWindowSize();
  const isMobile = deviceSize === DeviceSize.SMALL;

  useLockScrollWithOverscroll(isMobile);
  useChatWindowLockScroll();

  const ERROR_MESSAGE = 'Sorry, I encountered an error processing your request. Please try again.';

  const setAssistantMessage = useCallback(
    (_content: string, shouldAppend: boolean) => {
      setMessages((prev) => {
        const lastMessage = prev[prev.length - 1];
        const content = shouldAppend && lastMessage.role === 'assistant' ? lastMessage.content + _content : _content;

        if (lastMessage.role === 'assistant') {
          return [...prev.slice(0, -1), { role: 'assistant', content }];
        } else {
          return [...prev, { role: 'assistant', content }];
        }
      });
    },
    [setMessages]
  );

  const scrollToNewUserMessage = useCallback(() => {
    const height = chatBodyRef.current.clientHeight;
    setSpacerHeight(height);

    setTimeout(() => {
      requestAnimationFrame(() => {
        if (chatBodyRef.current && lastUserMessageRef.current) {
          const targetOffset = lastUserMessageRef.current.offsetTop;
          chatBodyRef.current.scrollTo({ top: targetOffset, behavior: 'smooth' });
        }
      });
    }, 300);
  }, []);

  const sendUserMessage = useCallback(
    async (userMessage: string) => {
      setIsLoading(true);
      setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
      scrollToNewUserMessage();
      setIsWaitingOnFirstChunk(true);

      Itly.productAssistantMessageSent({ user_message: userMessage, current_url: window.location.href });

      const requestData = {
        conversationId: conversationId,
        userMessage: userMessage,
        userId: getSplitUserKey()
      };

      try {
        const response = await fetch(process.env.GATSBY_CRUD_CORE_CHAT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        // First chunk is always conversationId
        const { done: streamDone, value } = await reader.read();
        let done = streamDone;
        const firstChunk = decoder.decode(value, { stream: true });

        setConversationId(firstChunk);

        let isFirstChunk = true;
        while (!done) {
          const { done: streamDone, value } = await reader.read();
          done = streamDone;

          if (value) {
            const chunk = decoder.decode(value, { stream: true });

            if (chunk.includes('ERROR:')) {
              setAssistantMessage(ERROR_MESSAGE, false);
              setIsWaitingOnFirstChunk(false);
              break;
            }

            setAssistantMessage(chunk, true);

            if (isFirstChunk) {
              isFirstChunk = false;
              setIsWaitingOnFirstChunk(false);
            }
          }
        }
      } catch (error) {
        console.error('Error streaming message:', error);
        setAssistantMessage(ERROR_MESSAGE, false);
      } finally {
        setSpacerHeight(0);
        setIsLoading(false);
      }
    },
    [conversationId, scrollToNewUserMessage, setAssistantMessage, setConversationId, setIsLoading, setMessages]
  );

  const handleSampleQuestionClick = useCallback(
    (question: string, index: number) => {
      const cleanedQuestion = question.replace(/^“|”$/g, '');
      Itly.productAssistantSampleQuestionClicked({
        current_url: window.location.href,
        sample_question: cleanedQuestion,
        index
      });
      sendUserMessage(cleanedQuestion);
    },
    [sendUserMessage]
  );

  const handleSendClick = useCallback(async () => {
    sendUserMessage(inputValue);
    setInputValue('');
  }, [inputValue, sendUserMessage, setInputValue]);

  const handleReset = useCallback(() => {
    Itly.productAssistantResetClicked({ current_url: window.location.href });
    setConversationId('');
    setInputValue('');
    setMessages([]);
  }, [setConversationId, setInputValue, setMessages]);

  const lastUserMessageIndex = useMemo(
    () =>
      [...messages]
        .map((m, i) => ({ ...m, i }))
        .reverse()
        .find((m) => m.role === 'user')?.i,
    [messages]
  );

  return (
    <>
      <ChatOverlay />
      <ChatLayout id="chat-layout" isExpanded={isExpanded}>
        <ChatContainer>
          <ChatHeader>
            <ChatTitleContainer>
              <ChatBetaNote />
              <ChatTitle>A|U Product Recommendation Assistant</ChatTitle>
            </ChatTitleContainer>
            <ChatClose />
            <ChatExpand />
          </ChatHeader>
          <ChatBody id="chat-body" ref={chatBodyRef}>
            <MessageBubble
              message={{
                role: 'assistant',
                content: "Hi, I am Artifact Uprising's Product Recommendation Assistant. How can I assist you today?" // eslint-disable-line quotes
              }}
            />
            {messages?.map((message, idx) => (
              <MessageBubble
                key={idx}
                message={message}
                ref={idx === lastUserMessageIndex ? lastUserMessageRef : undefined}
              />
            ))}
            {isWaitingOnFirstChunk && <TypingBubble />}
            <ScrollSpacer height={spacerHeight} />
          </ChatBody>
          <SampleQuestions handleClick={handleSampleQuestionClick} />
          <ChatFooter handleSendClick={handleSendClick} handleReset={handleReset} />
        </ChatContainer>
      </ChatLayout>
    </>
  );
};

export default ChatWindow;
